import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StudentLayout from "../Layout";
import OpeningUI from "../../../components/OpeningUI";
import {
  useGetModulesByClusterQuery,
  useGetStudentByEmailIdQuery,
  useGetCampusQuery,
  useGetAllModulesQuery,
} from "../../../graphql/generated.tsx";
import { Spinner } from "flowbite-react";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import Loader from "../../../components/Loader.jsx";
const Task = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const email = user?.email;

  // show intro once per logical page (Task) during this tab session
  const pageKey = "openingUI:task";
  const [showIntro, setShowIntro] = useState(() => {
    try {
      const map = window.__openingUIShownPages || {};
      return !map[pageKey];
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setShowIntro(false);
      // mark intro as shown for this logical page in this tab session
      try {
        window.__openingUIShownPages = window.__openingUIShownPages || {};
        window.__openingUIShownPages[pageKey] = true;
      } catch (e) {}
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  // don't run queries until intro has finished
  const { data: studentData } = useGetStudentByEmailIdQuery({
    variables: { email },
    skip: showIntro || !email,
  });
  const student = studentData?.getStudentByEmailId;
  const clusterId = student?.clusterIds?.[0];

  const { data: campusData } = useGetCampusQuery({
    variables: { id: student?.campusId },
    skip: showIntro || !student,
  });
  const campusName = campusData?.campus?.name;

  const studentId = user?.uid;

  const {
    data: clusterModulesData,
    loading: clusterModulesLoading,
    error: clusterModulesError,
  } = useGetModulesByClusterQuery({
    variables: { campusName, clusterId },
    skip: showIntro || !campusName || !clusterId,
  });

  const {
    data: allModulesData,
    loading: allModulesLoading,
    error: allModulesError,
  } = useGetAllModulesQuery({
    skip: showIntro,
  });

   const [Isload, setIsload] = useState(true);

   useEffect(()=>{
    setTimeout(()=>{
      setIsload(false);
    },2000);
  },[]);

  // show opening UI inside StudentLayout while intro timer is active
  if (showIntro) {
    return (
      <StudentLayout>
        <OpeningUI />
      </StudentLayout>
    );
  }

  if (!clusterId || !campusName || clusterModulesLoading || allModulesLoading || Isload) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (clusterModulesError)
    return (
      <div>Error loading cluster modules: {clusterModulesError.message}</div>
    );
  if (allModulesError)
    return <div>Error loading all modules: {allModulesError.message}</div>;

  const dummyData = {
    title: "Unnamed Module",
    description: "No description available.",
    domain: "Unknown Domain",
    numQuestions: 0,
    days: [],
  };

  const completedQuestionIds = new Set(student?.completedQuestions || []);

  const tasks =
    clusterModulesData?.getModulesByCluster?.map((clusterModule) => {
      const detailedModule = allModulesData?.getAllModules?.find(
        (allModule) => allModule._id === clusterModule.moduleID
      );

      const moduleQuestionIds =
        detailedModule?.daywise_plan?.flatMap(
          (day) => day.questions || []
        ) || [];

      const numQuestions = moduleQuestionIds.length;
      const completedCount = moduleQuestionIds.filter((qid) =>
        completedQuestionIds.has(qid)
      ).length;

      return {
        id: clusterModule.moduleID,
        title: detailedModule?.name || clusterModule.name || dummyData.title,
        description:
          detailedModule?.short_description ||
          clusterModule.description ||
          dummyData.description,
        domain: detailedModule?.domain || dummyData.domain,
        numQuestions: numQuestions,
        completedCount,
        days: detailedModule?.daywise_plan || dummyData.days,
        enabled_dates: clusterModule.enabled_dates || [],
      };
    }) || [];

  
  return (
    <StudentLayout>
      <NetworkIndicator />
      <div className="min-h-screen space-y-8 bg-white">
        <div className="p-6 space-y-4 text-center max-md:p-4">
          <h1 className="text-5xl font-extrabold max-md:text-4xl text-primary">Practice lab </h1>
          <p className="text-lg text-gray-700">
            Explore  your modules . Stay on track and complete your learning journey !
          </p>
          {tasks.length === 0 ? (
            <p className="mt-2 text-sm text-orange-600">
              No modules found for your cluster yet.
            </p>
          ) : null}
        </div>
  <div className="w-full px-4 py-8 bg-white md:px-8">

          {/* Task List */}
          {tasks.length === 0 ? (
            <div className="text-center text-gray-600">
              <div className="py-12">
                <div className="mb-2 text-lg">No modules available</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-3">
              {tasks.map((task, idx) => (
                <div
                  key={idx}
                  className="relative bg-gradient-to-br from-white via-blue-50 to-blue-100 rounded-2xl border border-blue-100 flex flex-col min-h-[270px] overflow-hidden hover:shadow-lg transition-shadow cursor-pointer w-full"
                  onClick={() =>
                    navigate("/practice-lab/display-practice-lab", {
                      state: {
                        moduleID: task.id,
                        enabled_dates: task.enabled_dates,
                        studentId,
                        clusterId,
                        campusName,
                        moduleName: task.title,
                        moduleDescription: task.description,
                        moduleDomain: task.domain,
                        numQuestions: task.numQuestions,
                      },
                    })
                  }
                >
                  <div className="flex flex-col flex-1 gap-5 px-6 pt-6 pb-0">
                    <div
                      className="text-xl font-extrabold text-blue-900 truncate"
                      title={task.title}
                    >
                      {task.title}
                    </div>
                    <div className="h-24 overflow-hidden text-base text-gray-700 line-clamp-4">
                      {task.description}
                    </div>
                  </div>
                  <div className="flex w-full px-0 pt-2 pb-0 mt-auto">
                    <div className="grid items-center flex-1 grid-cols-3 py-2 border-t-2 border-blue-400 sm:py-3">
                      <span className="text-xs font-bold text-center text-blue-900 sm:text-sm">
                        {task.domain}
                      </span>
                      <span className="text-xs font-semibold text-center text-blue-900 sm:text-sm">
                        Question <span className="text-sm font-bold text-blue-900 sm:text-base">{task.numQuestions}</span>
                      </span>
                      <span className="text-xs font-semibold text-center text-blue-900 sm:text-sm">
                        Days <span className="text-sm font-bold text-blue-900 sm:text-base">{task.days?.length || 0}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default Task;